import { message } from "antd";
import axios, { AxiosHeaders } from "axios";
import { BACKEND_API } from "./constants/APP_CONSTANTS";
import { TokenStorage, UserStorage } from "./utils/cookieStorage";
import { clearDicomProxy } from "./utils/dicomProxy";
import { sessionManager } from "./utils/sessionManager";

export const BASE_API = BACKEND_API;
export const BASE_URL = BASE_API;

axios.defaults.withCredentials = true;            // send cookies
axios.defaults.xsrfCookieName = "XSRF-TOKEN";     // cookie name from backend
axios.defaults.xsrfHeaderName = "x-csrf-token";   // header Fastify expects

// Create axios instance with secure configuration
const axiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 30000, // 30 seconds timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add authentication and security headers
axiosInstance.interceptors.request.use(
  function (config) {
    const token = TokenStorage.getAccessToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (config.method !== 'get')  {
      const csrfToken = sessionStorage.getItem('csrf_token');
      console.log("CSRF Token from session storage:", csrfToken);
      if (csrfToken)  {
        config.headers['x-csrf-token'] = csrfToken;
      }
    }
    return config;
  },
  function (error) {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and token refresh
axiosInstance.interceptors.response.use(
  (response) => {
    // Track successful API calls as user activity
    sessionManager.extendSession();
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle network errors
    if (!error.response) {
      console.error('Network error:', error);
      message.error('Network error. Please check your connection.');
      return Promise.reject(error);
    }

    const { status, data } = error.response;

    // A 403 permission denial is an authorization failure, not a session
    // problem — fall through to the generic 4xx handling so the server's
    // message is shown, instead of refreshing tokens and logging the user out.
    const isPermissionDenial = status === 403 && data?.error === 'PERMISSION_DENIED';

    // Handle authentication errors
    if ((status === 401 || status === 403) && !isPermissionDenial) {
      // Skip authentication error handling if on login page
      const isLoginPage = window.location.pathname === '/login';

      // If on login page and this is a CSRF token request, just reject without redirect
      if (isLoginPage && originalRequest.url.includes('/csrf-token')) {
        console.error('CSRF token fetch failed on login page:', error.response?.data);
        return Promise.reject(error);
      }
      
      // Check for SESSION_TERMINATED error code
      if (data?.code === 'SESSION_TERMINATED') {
        handleSessionTerminated();
        return Promise.reject(error);
      }

      // Prevent infinite retry loop
      if (originalRequest._retry) {
        handleAuthenticationError();
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      // Try to refresh token (skip if on login page)
      const refreshToken = TokenStorage.getRefreshToken();
      if (refreshToken && !originalRequest.url.includes('/auth/refresh') && !isLoginPage) {
        try {
          await refreshAccessToken();
          // Retry original request with new token
          const newToken = TokenStorage.getAccessToken();
          if (newToken) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return axiosInstance(originalRequest);
          }
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
        }
      }

      handleAuthenticationError();
      return Promise.reject(error);
    }

    // Handle other HTTP errors
    const isLoginPage = window.location.pathname === '/login';

    if (status >= 400 && status < 500) {
      const errorMessage = data?.message || `Client error: ${status}`;
      console.error('Client error:', errorMessage);

      // Don't show generic error messages on login page - let login page handle it
      if (!isLoginPage || !originalRequest.url.includes('/csrf-token')) {
        message.error(errorMessage);
      }
    } else if (status >= 500) {
      const errorMessage = data?.message || 'Server error. Please try again later.';
      console.error('Server error:', errorMessage);

      // Don't show generic error messages on login page - let login page handle it
      if (!isLoginPage || !originalRequest.url.includes('/csrf-token')) {
        message.error(errorMessage);
      }
    }

    return Promise.reject(error);
  }
);

// Helper function to refresh access token
async function refreshAccessToken() {
  const refreshToken = TokenStorage.getRefreshToken();
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  const response = await axios.post(`${BASE_API}/auth/refresh`, {
    refresh_token: refreshToken
  });

  const { access_token, expires_in, refresh_token: newRefreshToken } = response.data;

  TokenStorage.setAccessToken(access_token, expires_in);
  if (newRefreshToken) {
    TokenStorage.setRefreshToken(newRefreshToken);
  }

  return access_token;
}

// Helper function to handle authentication errors
function handleAuthenticationError() {
  // Don't redirect if already on login page
  const currentPath = window.location.pathname;
  if (currentPath === '/login') {
    console.log('Already on login page, skipping redirect');
    return;
  }

  // Store current path for redirect after login
  sessionStorage.setItem("path_before_login", currentPath);

  // Clear authentication data
  TokenStorage.clearAllTokens();
  UserStorage.removeUserDetails();
  clearDicomProxy();
  sessionStorage.removeItem('csrf_token');

  // Show user-friendly message
  message.error("Your session has expired. Please log in again.");

  // Redirect to login after delay
  setTimeout(() => {
    window.location.href = '/login';
  }, 2000);
}

// Helper function to handle session termination (force logout)
function handleSessionTerminated() {
  // Store current path for redirect after login
  const currentPath = window.location.pathname;
  if (currentPath !== '/login') {
    sessionStorage.setItem("path_before_login", currentPath);
  }

  // Clear authentication data
  TokenStorage.clearAllTokens();
  UserStorage.removeUserDetails();
  clearDicomProxy();
  sessionStorage.removeItem('csrf_token');

  // Show specific message for session termination
  message.warning({
    content: "Your session has been terminated because you logged in from another device.",
    duration: 5,
    style: {
      marginTop: '20vh',
    }
  });

  // Redirect to login after delay
  setTimeout(() => {
    window.location.href = '/login';
  }, 3000);
}

export default axiosInstance;
